import { List, type OrderedMap } from 'immutable';
import { useCallback, useMemo, useState } from 'react';
import type { UserResponse } from 'stream-chat';
import { useChatContext } from 'stream-chat-react';

import { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import FontIcon from '@/shared/ui/FontIcon';
import { useWiwDispatch, useWiwSelector } from '@/store';
import { generateStreamChannelId } from '@/workchat/utils';
import type { HeaderButton } from '@/workchat/v2/components/HeaderV2';
import { VIEW_CONVERSATION } from '@/workchat/v2/components/ViewConversationV2';
import { useStreamProvider } from '@/workchat/v2/providers/StreamProvider';
import { setConversation, viewStackPop, viewStackPush, viewStackReset } from '@/workchat/v2/store/reducer';
import HeaderV3 from '@/workchat/v3/components/HeaderV3/HeaderV3';
import ChannelNameInput from '@/workchat/v3/components/NewChannel/ChannelNameInput/ChannelNameInput';
import { UserPickerV3 } from '@/workchat/v3/components/UserPickerV3/UserPickerV3';

import '@/workchat/v3/components/NewChannel/NewChannel.scss';

export interface NewChannelProps {
  preselectedUsers?: List<string>;
  userCache?: OrderedMap<string, UserResponse>;
}

export default function NewChannel({ preselectedUsers, userCache }: NewChannelProps) {
  const dispatch = useWiwDispatch();
  const account = useWiwSelector(getAuthAccount);
  const user = useWiwSelector(getAuthUser);

  const { client } = useStreamProvider();
  const { setActiveChannel } = useChatContext();

  const [channelName, setChannelName] = useState<string>('');
  const [selectedUsers, setSelectedUsers] = useState<List<string>>(preselectedUsers || List<string>());
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const handleChannelNameChange = useCallback((newChannelName: string) => {
    setChannelName(newChannelName);
    setError(null); // Clear error when user modifies channel name
  }, []);

  const handleUsersChange = useCallback((newSelectedUsers: List<string>) => {
    setSelectedUsers(newSelectedUsers);
  }, []);

  const validateInputs = (): boolean => {
    return channelName.trim().length > 0 && selectedUsers.size > 0;
  };

  const handleCreateChannel = async () => {
    if (!validateInputs()) {
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const channelId = generateStreamChannelId(account.id, channelName);
      const channels = await client?.queryChannels({ id: channelId });

      if (channels && channels.length > 0) {
        setError('Channel name already exists.');
        setIsCreating(false);
        return;
      }

      const channel = client?.channel('chat', channelId, {
        members: selectedUsers
          .toArray()
          .map(id => `${id}`)
          .concat(`${user.id}`),
        team: `${account.id}`,
        name: channelName,
      });

      await channel?.create();

      if (channel?.id) {
        setActiveChannel(channel);
        dispatch(viewStackReset());
        dispatch(setConversation(channel.id));
        dispatch(viewStackPush(VIEW_CONVERSATION));
      } else {
        throw new Error('No channel created');
      }
    } catch (_error: any) {
      setError('Failed to create channel. Please try again.');
      setIsCreating(false);
    }
  };

  const handleBack = useCallback(() => {
    dispatch(viewStackPop());
  }, [dispatch]);

  const isNextDisabled = !validateInputs() || isCreating || !!error;

  const buttons: HeaderButton[] = useMemo(
    () => [
      {
        title: 'Back',
        action: handleBack,
        label: [<FontIcon className="mr-1" key="back" icon="chevron-left" />, 'Back'],
        side: 'left',
        color: 'secondary',
      },
      {
        action: handleCreateChannel,
        label: 'Next',
        disabled: isNextDisabled,
        side: 'right',
        color: 'primary',
      },
    ],
    [handleBack, handleCreateChannel, isCreating, isNextDisabled],
  );

  return (
    <div className="NewChannel">
      <HeaderV3 buttons={buttons}>New Channel</HeaderV3>
      <div className="channel-form">
        <ChannelNameInput
          autoFocus
          value={channelName}
          onChange={handleChannelNameChange}
          error={error}
          disabled={isCreating}
        />
        <UserPickerV3
          user={user}
          account={account}
          onUserAdded={handleUsersChange}
          enableFilters
          collapsible
          prefillUserIds={selectedUsers}
          userCache={userCache}
        />
      </div>
    </div>
  );
}
